/**
 * /pay/$token — public invoice view + Pay Now.
 * Unauthenticated. Fetches invoice, renders InvoiceCard, initiates Stripe checkout.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { usePublicInvoice } from '@/hooks/usePublicInvoice';
import { createCheckoutSession } from '@/services/publicPayApi';
import { BrandHeader } from '@/components/payment/BrandHeader';
import { InvoiceCard } from '@/components/payment/InvoiceCard';
import { PaymentErrorState } from '@/components/payment/PaymentErrorState';
import type { PaymentErrorReason } from '@/components/payment/PaymentErrorState';
import { PaymentFooter } from '@/components/payment/PaymentFooter';

export const Route = createFileRoute('/pay/$token')({
  component: PayTokenPage,
});

const CHECKOUT_ERRORS: Record<number, string> = {
  429: 'Too many checkout attempts. Please wait a minute and try again.',
  502: 'Payment service is temporarily unavailable. Please try again.',
  404: 'This payment link is no longer valid. Please contact us for a new one.',
};

function mapReason(reason: string | undefined): PaymentErrorReason {
  const valid: PaymentErrorReason[] = ['not_found', 'expired', 'used', 'invoice_not_payable'];
  return (valid.find((r) => r === reason) as PaymentErrorReason | undefined) ?? 'generic';
}

function PayTokenPage() {
  const { token } = Route.useParams();
  const { data: result, isLoading } = usePublicInvoice(token);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <PageShell>
        <div
          className="space-y-4 animate-pulse bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
          aria-busy="true"
          aria-label="Loading invoice"
        >
          {[0.67, 0.33, 0.25].map((w, i) => (
            <div key={i} className={`h-4 bg-gray-200 dark:bg-gray-700 rounded`} style={{ width: `${w * 100}%` }} />
          ))}
          <div className="h-px bg-gray-100 dark:bg-gray-800 my-2" />
          {[1, 1, 0.8].map((w, i) => (
            <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded" style={{ width: `${w * 100}%` }} />
          ))}
          <div className="h-px bg-gray-100 dark:bg-gray-800 my-2" />
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded mt-2" />
        </div>
      </PageShell>
    );
  }

  if (!result || !result.ok) {
    const reason = mapReason(result?.ok === false ? result.reason : undefined);
    return (
      <PageShell>
        <PaymentErrorState reason={reason} />
      </PageShell>
    );
  }

  async function handlePayNow() {
    setIsCheckingOut(true);
    setCheckoutError(null);
    const checkout = await createCheckoutSession(token);
    if (checkout.ok) {
      window.location.href = checkout.data.checkoutUrl;
      return;
    }
    setIsCheckingOut(false);
    setCheckoutError(
      CHECKOUT_ERRORS[checkout.status] ?? 'Something went wrong. Please try again or contact us.',
    );
  }

  return (
    <PageShell>
      <InvoiceCard
        invoice={result.data}
        onPayNow={handlePayNow}
        isCheckingOut={isCheckingOut}
        checkoutError={checkoutError}
      />
      <PaymentFooter />
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-start px-4 py-10">
      <div className="w-full max-w-lg">
        <BrandHeader />
        <main>{children}</main>
      </div>
    </div>
  );
}
